import type { V1ThreadMessage } from '@/generated/hooks/zen/models';
import {
  ChatStatus,
  EMessageDirection,
  EMessageStatus,
  EMessageType,
  EWidgetType,
  Icon,
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  Markdown,
  type IChatAttachment,
  type IChatMessage,
} from '@kognitos/lattice';
import { captureException } from '@/modules/sentry';
import { getCurrentDate } from '@/shared/utils/datetime-utils';
import { getFileDisplayName } from '@/shared/utils/file-utils';
import { getInternalToolCallDescription } from '@/shared/utils/tool-call-utils';
import i18n from '@/shared/i18n';
import {
  ChatIntegrationConnection,
  ChatFeedback,
  ChatOutputRenderer,
  ChatBrowserSessionWidget,
  QuillToolCallWidget,
  QuillTodoProgressWidget,
  type IQuillTodoProgressItem,
  type TQuillTodoStatus,
} from '../components';
import {
  detectFileType,
  mapFileTypeToAttachmentType,
} from '@/modules/file-renderer/utils/file-type-detection';
import type {
  IProcessThreadMessageParams,
  IQuillChatAddConnectionMessage,
  IQuillChatAutomateRequestMessage,
  IQuillChatAutomationBuiltMessage,
  IQuillChatConnectionRequestMessage,
  IQuillChatConnectionResponseMessage,
  IQuillChatDefaultInputsMessage,
  IQuillChatExecutionReferenceMessage,
  IQuillChatSelectedConnection,
  IQuillChatStateCompleteMessage,
} from '../types';

/**
 * Result of processing batch messages
 */
export interface IBatchMessagesResult {
  /** New chat messages to add (with connection updates already applied) */
  messages: IChatMessage[];
  /** Whether processing is still ongoing */
  isProcessing: boolean;
  /** Whether any automation was built during processing */
  shouldInvalidateAutomation: boolean;
}

/**
 * Callbacks for processing batch messages
 */
export interface IBatchMessagesCallbacks {
  /** Callback for connection selection */
  onConnectionSelect: (
    connection: IQuillChatSelectedConnection,
    connectionRequest: IQuillChatConnectionRequestMessage,
    messageId: string,
  ) => void;
  /** Callback for file clicks */
  onFileClick?: (file: string) => void;
  /** Set of already processed message IDs */
  processedMessageIds: Set<string>;
  /** Status message for thinking state */
  existingMessages: IChatMessage[];
  /** Map to track add_connection messages across batches */
  addConnectionQueue: Map<
    string,
    {
      connectionId: string;
      bookName: string;
      bookVersion: string;
    }
  >;
  /** Thread ID for feedback submission */
  threadId?: string;
  /** Automation ID for scoped feedback submission */
  automationId: string;
}

export function convertStringToQuillMessage(message: string): V1ThreadMessage {
  return {
    user_message: {
      user_message_type: 'user_query',
      content_list: {
        items: [{ text: message.trim() }],
      },
    },
  };
}

export function convertFileAndMessageToQuillMessage(
  bishopFileUrls: string[],
  message: string,
): V1ThreadMessage {
  const items: Array<{ text: string }> = [];

  // Add message text if provided
  if (message.trim().length > 0) {
    items.push({ text: message.trim() });
  }

  // Add file upload action for each file URL
  for (const bishopFileUrl of bishopFileUrls) {
    items.push({
      text: getFileUploadMessageText(bishopFileUrl),
    });
  }

  return {
    user_message: {
      user_message_type: 'user_query',
      content_list: {
        items,
      },
    },
  };
}

export function getConnectionResponseMessage(
  response: IQuillChatConnectionResponseMessage,
  messageId: string,
): V1ThreadMessage {
  return {
    user_message: {
      user_message_type: 'user_query',
      content_list: {
        items: [
          { text: getConnectionResponseMessageText(response, messageId) },
        ],
      },
    },
  };
}

function getConnectionResponseMessageText(
  response: IQuillChatConnectionResponseMessage,
  messageId: string,
): string {
  return `
  <user_action type="add_connection">
    <book_name>${response.book_name}</book_name>
    <book_version>${response.book_version}</book_version>
    <connection_id>${response.connection_id}</connection_id>
    <message_id>${messageId}</message_id>
  </user_action>`;
}

const getFileUploadMessageText = (bishopFileUrl: string): string => {
  return `<user_action type="file_upload"><file_resource>${bishopFileUrl}</file_resource></user_action>`;
};

interface IToolCallInput {
  description?: unknown;
}

const COUNTABLE_INTERNAL_TOOL_DESCRIPTION_KEYS = [
  'automation.quillChat.tool.internal.edit',
  'automation.quillChat.tool.internal.glob',
  'automation.quillChat.tool.internal.grep',
  'automation.quillChat.tool.internal.ls',
  'automation.quillChat.tool.internal.read',
  'automation.quillChat.tool.internal.write',
] as const;

const INTERNAL_TOOL_COUNT_REGEX = /, (\d+) files$/;

interface ITodoProgressInput {
  todos?: unknown;
  tool_input?: unknown;
  input?: unknown;
}

interface ITodoProgressItemInput {
  id?: unknown;
  content?: unknown;
  text?: unknown;
  task?: unknown;
  title?: unknown;
  status?: unknown;
  state?: unknown;
}

const TODO_PROGRESS_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'in_progress',
  'completed',
  'cancelled',
]);

const TODO_PROGRESS_STATUS_ALIASES: Readonly<Record<string, TQuillTodoStatus>> =
  {
    canceled: 'cancelled',
    cancelled: 'cancelled',
    complete: 'completed',
    completed: 'completed',
    done: 'completed',
    inProgress: 'in_progress',
    in_progress: 'in_progress',
    inprogress: 'in_progress',
    pending: 'pending',
  };

const TODO_PROGRESS_MESSAGE_ID_PREFIX = 'todo-progress';

/**
 * Creates the stable chat message ID used to update todo progress in place.
 *
 * @param turnId - Resource name for the user turn that owns the todo list
 * @returns Stable todo-progress message ID
 */
export function getTodoProgressMessageId(turnId: string): string {
  return `${TODO_PROGRESS_MESSAGE_ID_PREFIX}:${turnId}`;
}

/**
 * Parses a todo_progress artifact payload into renderable todo items.
 *
 * @param content - JSON string from the artifact content
 * @returns Valid todo items, or an empty array when the payload is invalid
 */
export function parseTodoProgressItems(
  content?: string,
): IQuillTodoProgressItem[] {
  if (!content) {
    return [];
  }

  try {
    const parsed = JSON.parse(content) as ITodoProgressInput;
    const nestedToolInput = parsed.tool_input as ITodoProgressInput | undefined;
    const nestedInput = parsed.input as ITodoProgressInput | undefined;
    const todos = parsed.todos ?? nestedToolInput?.todos ?? nestedInput?.todos;
    if (!Array.isArray(todos)) {
      return [];
    }

    return todos.flatMap(
      (todo: unknown, index: number): IQuillTodoProgressItem[] => {
        const item = todo as ITodoProgressItemInput;
        const rawContent = item.content ?? item.text ?? item.task ?? item.title;
        const rawStatus = item.status ?? item.state ?? 'pending';
        const normalizedStatus =
          typeof rawStatus === 'string'
            ? (TODO_PROGRESS_STATUS_ALIASES[
                rawStatus.replace(/[-_\s]/g, '').toLowerCase()
              ] ?? rawStatus)
            : undefined;
        if (
          typeof rawContent !== 'string' ||
          typeof normalizedStatus !== 'string' ||
          !TODO_PROGRESS_STATUSES.has(normalizedStatus)
        ) {
          return [];
        }

        return [
          {
            id:
              typeof item.id === 'string' && item.id.length > 0
                ? item.id
                : `todo-${index}`,
            content: rawContent,
            status: normalizedStatus as TQuillTodoStatus,
          },
        ];
      },
    );
  } catch {
    return [];
  }
}

/**
 * Creates the chat message used to display todo progress.
 *
 * @param params - Todo progress message parameters
 * @returns Todo progress chat message, or null when content is invalid
 */
export function createTodoProgressChatMessage({
  messageId,
  timestamp,
  content,
}: {
  messageId: string;
  timestamp: string;
  content?: string;
}): IChatMessage | null {
  const todos = parseTodoProgressItems(content);
  if (todos.length === 0) {
    return null;
  }

  return {
    id: messageId,
    content: i18n.t('automation.quillChat.todo.title', {
      ns: 'automation',
    }),
    direction: EMessageDirection.INCOMING,
    timestamp,
    widget: {
      type: EWidgetType.CUSTOM,
      customContent: <QuillTodoProgressWidget todos={todos} />,
    },
  };
}

/**
 * Extracts the user-facing action description from a Quill tool-call payload.
 *
 * @param input - JSON string from ToolCallRequest.input
 * @returns Tool description when present
 */
function getToolCallDescription(input?: string): string {
  if (!input) {
    return '';
  }

  try {
    const parsed = JSON.parse(input) as IToolCallInput;
    return typeof parsed.description === 'string' ? parsed.description : '';
  } catch {
    return '';
  }
}

/**
 * Gets the base internal tool description from a rendered message.
 *
 * @param content - Chat message content
 * @returns Base internal tool description when countable
 */
function getCountableInternalToolDescription(content?: string): string {
  if (!content) {
    return '';
  }

  for (const key of COUNTABLE_INTERNAL_TOOL_DESCRIPTION_KEYS) {
    const description = i18n.t(key, { ns: 'automation' });
    if (
      content === description ||
      content.replace(INTERNAL_TOOL_COUNT_REGEX, '') === description
    ) {
      return description;
    }
  }

  return '';
}

/**
 * Gets the number of tool calls already represented by a message.
 *
 * @param content - Chat message content
 * @returns Count represented by the message
 */
function getInternalToolCount(content?: string): number {
  const match = content?.match(INTERNAL_TOOL_COUNT_REGEX);
  return match?.[1] ? Number(match[1]) : 1;
}

/**
 * Formats an internal tool description with a file count.
 *
 * @param description - Base tool description
 * @param count - Number of consecutive tool calls represented
 * @returns User-facing description
 */
function formatInternalToolDescriptionWithCount(
  description: string,
  count: number,
): string {
  if (count <= 1) {
    return description;
  }

  return i18n.t('automation.quillChat.tool.internal.fileCount', {
    ns: 'automation',
    description,
    count,
  });
}

/**
 * Coalesces adjacent internal tool-call widgets with the same description.
 *
 * @param messages - Chat messages in display order
 * @returns Messages with consecutive countable internal tool calls merged
 */
export function mergeConsecutiveInternalToolCallMessages(
  messages: IChatMessage[],
): IChatMessage[] {
  return messages.reduce<IChatMessage[]>((mergedMessages, message) => {
    const description = getCountableInternalToolDescription(message.content);
    const previousMessage = mergedMessages[mergedMessages.length - 1];
    const previousDescription = getCountableInternalToolDescription(
      previousMessage?.content,
    );

    if (
      description &&
      previousMessage &&
      previousDescription === description &&
      previousMessage.widget?.type === EWidgetType.CUSTOM &&
      message.widget?.type === EWidgetType.CUSTOM
    ) {
      const count =
        getInternalToolCount(previousMessage.content) +
        getInternalToolCount(message.content);
      mergedMessages[mergedMessages.length - 1] = createToolCallChatMessage({
        messageId: previousMessage.id,
        timestamp:
          typeof previousMessage.timestamp === 'string'
            ? previousMessage.timestamp
            : getCurrentDate().toISOString(),
        description: formatInternalToolDescriptionWithCount(description, count),
        isActive:
          previousMessage.status === EMessageStatus.SENDING ||
          message.status === EMessageStatus.SENDING,
      });
      return mergedMessages;
    }

    mergedMessages.push(message);
    return mergedMessages;
  }, []);
}

/**
 * Creates a tool-call widget chat message.
 *
 * @param params - Tool-call message parameters
 * @returns Chat message for a tool-call progress widget
 */
function createToolCallChatMessage({
  messageId,
  timestamp,
  description,
  toolName,
  isActive,
}: {
  messageId: string;
  timestamp: string;
  description: string;
  toolName?: string;
  isActive: boolean;
}): IChatMessage {
  return {
    id: messageId,
    content: description,
    direction: EMessageDirection.INCOMING,
    timestamp,
    status: isActive ? EMessageStatus.SENDING : EMessageStatus.SENT,
    widget: {
      type: EWidgetType.CUSTOM,
      customContent: (
        <QuillToolCallWidget
          description={description}
          toolName={toolName}
          startedAt={timestamp}
          isActive={isActive}
        />
      ),
    },
  };
}

/**
 * Marks active tool-call widgets as completed when the agent turn finishes.
 *
 * @param messages - Existing chat messages
 * @returns Messages with active tool-call widgets completed
 */
export function completeActiveToolCallMessages(
  messages: IChatMessage[],
): IChatMessage[] {
  return messages.map(message => {
    if (
      message.status !== EMessageStatus.SENDING ||
      message.widget?.type !== EWidgetType.CUSTOM
    ) {
      return message;
    }

    return createToolCallChatMessage({
      messageId: message.id,
      timestamp:
        typeof message.timestamp === 'string'
          ? message.timestamp
          : getCurrentDate().toISOString(),
      description: message.content ?? '',
      isActive: false,
    });
  });
}

/**
 * Keeps only the latest displayed tool-call widget active.
 *
 * @param messages - Messages in display order
 * @returns Messages with only the final tool-call widget active, if it is last
 */
export function reconcileToolCallActivity(
  messages: IChatMessage[],
): IChatMessage[] {
  const latestToolCallIndex =
    messages.length > 0 &&
    messages[messages.length - 1].status === EMessageStatus.SENDING &&
    messages[messages.length - 1].widget?.type === EWidgetType.CUSTOM
      ? messages.length - 1
      : -1;

  return messages.map((message, index) => {
    if (
      message.status !== EMessageStatus.SENDING ||
      message.widget?.type !== EWidgetType.CUSTOM
    ) {
      return message;
    }

    return createToolCallChatMessage({
      messageId: message.id,
      timestamp:
        typeof message.timestamp === 'string'
          ? message.timestamp
          : getCurrentDate().toISOString(),
      description: message.content ?? '',
      isActive: index === latestToolCallIndex,
    });
  });
}

export function processIncomingMessage(message: string) {
  // 1. Unescape common escape chars
  const cleaned = message.replace(/\\n/g, '\n').replace(/\\t/g, '\t');

  // 2. Extract user_action type
  const actionMatch = cleaned.match(/<user_action[^>]*type="([^"]+)"[^>]*>/);
  const action = actionMatch && actionMatch[1] ? actionMatch[1] : 'message';

  if (action === 'message') {
    return { action, message };
  }

  // 3. Extract content inside <user_action> ... </user_action>
  const innerMatch = cleaned.match(
    /<user_action[^>]*>([\s\S]*?)<\/user_action>/,
  );
  const inner = innerMatch ? innerMatch[1] : '';

  // 4. Match ALL tags inside user_action
  const tagRegex = /<([a-zA-Z_]+)>([\s\S]*?)<\/\1>/g;

  const result: Record<string, string> = { action };

  let match;
  while ((match = tagRegex.exec(inner)) !== null) {
    const tag = match[1];
    const value = match[2].trim();
    result[tag] = value;
  }

  return result;
}

/**
 * Creates an IChatAttachment from a file resource name.
 *
 * @param {string} fileResource - Bishop file resource name
 * @returns {IChatAttachment} Chat attachment object
 */
function createAttachmentFromFileResource(
  fileResource: string,
  onFileClick?: (file: string) => void,
): IChatAttachment {
  // Extract filename from resource name if possible
  // Format: organizations/{org}/files/{file}
  const parts = fileResource.split('/');
  const fileName = parts.length > 0 ? parts[parts.length - 1] : 'File';
  const cleanFileName = getFileDisplayName(fileName);

  // Detect file type from filename extension
  const fileType = detectFileType(cleanFileName);
  const attachmentType = mapFileTypeToAttachmentType(fileType);

  return {
    id: fileResource || fileName,
    name: cleanFileName,
    type: attachmentType,
    onClick: () => onFileClick?.(fileResource),
  };
}

export function convertEventToChatMessage(
  event: V1ThreadMessage,
  onFileClick?: (file: string) => void,
):
  | IChatMessage
  | IQuillChatConnectionRequestMessage
  | IQuillChatAutomateRequestMessage
  | IQuillChatExecutionReferenceMessage
  | IQuillChatAutomationBuiltMessage
  | IQuillChatAddConnectionMessage
  | IQuillChatDefaultInputsMessage
  | IQuillChatStateCompleteMessage
  | null {
  // Use event.name as message ID, fallback to generated ID if not available
  const messageId = event.name || `msg-${crypto.randomUUID()}`;
  // Use update_time, fallback to create_time, then current date
  const timestamp =
    event.update_time || event.create_time || getCurrentDate().toISOString();

  // Handle user messages (outgoing)
  if (event.user_message) {
    const contentListItems = event.user_message.content_list?.items;
    if (!contentListItems || contentListItems.length === 0) {
      return null;
    }

    // Process all items to collect text content and attachments
    const textParts: string[] = [];
    const attachments: IChatAttachment[] = [];

    for (const item of contentListItems) {
      if (item.text) {
        const processed = processIncomingMessage(item.text);

        if (processed.action === 'build_automation') {
          return {
            type: 'build_automation',
            messageId,
            timestamp,
          };
        }

        // Handle add_connection action - used to update existing connection_request
        if (processed.action === 'add_connection' && processed.message_id) {
          return {
            type: 'add_connection',
            book_name: processed.book_name || '',
            book_version: processed.book_version || '',
            connection_id: processed.connection_id || '',
            message_id: processed.message_id,
            messageId,
            timestamp,
          };
        }

        if (processed.action === 'file_upload' && processed.file_resource) {
          // Create attachment for file upload
          attachments.push(
            createAttachmentFromFileResource(
              processed.file_resource,
              onFileClick,
            ),
          );
        } else if (processed.action === 'message' && processed.message) {
          // Add text content
          textParts.push(processed.message);
        } else if (!processed.action || processed.action === 'message') {
          // Fallback: treat as regular message text
          const messageText = processed.message || item.text;
          if (messageText.trim()) {
            textParts.push(messageText);
          }
        }
      }
    }

    // Combine text parts, as per the current implementation
    // There will be only one text part.
    // Keeping this for future compatibility.
    const content = textParts.length > 0 ? textParts.join('\n') : undefined;

    // Don't create message if there's no content and no attachments
    if (!content && attachments.length === 0) {
      return null;
    }

    return {
      id: messageId,
      content,
      direction: EMessageDirection.OUTGOING,
      timestamp,
      attachments: attachments.length > 0 ? attachments : undefined,
    };
  }

  // Handle agent messages (incoming)
  if (event.agent_message) {
    const agentContent = event.agent_message.content || '';
    if (!agentContent.trim()) {
      return null;
    }

    return {
      id: messageId,
      content: agentContent,
      direction: EMessageDirection.INCOMING,
      timestamp,
    };
  }

  // Handle progress_notification (thinking messages)
  if (event.progress_notification?.progress_type === 'thinking') {
    const content = event.progress_notification.content || '';
    if (!content.trim()) {
      return null;
    }

    return {
      id: messageId,
      content,
      direction: EMessageDirection.INCOMING,
      timestamp,
      type: EMessageType.THINKING,
    };
  }

  // Handle tool-call progress. Quill stores the user-facing action description
  // inside ToolCallRequest.input as JSON.
  if (event.tool_call_request) {
    const description =
      getInternalToolCallDescription(event.tool_call_request.display_name) ||
      getToolCallDescription(event.tool_call_request.input);
    if (!description.trim()) {
      return null;
    }

    return createToolCallChatMessage({
      messageId,
      timestamp,
      description,
      toolName: event.tool_call_request.display_name,
      isActive: event.state === 'STATE_STREAMING',
    });
  }

  // Handle connection request artifacts
  if (event.artifact && event.artifact.artifact_type === 'connection_request') {
    const content = event.artifact.content;
    if (!content) {
      return null;
    }

    try {
      const contentJson = JSON.parse(content);
      const bookName = contentJson.name;
      const bookVersion = contentJson.version;

      if (!bookName || !bookVersion) {
        return null;
      }

      // Return custom format for connection request
      return {
        type: 'connection_request',
        book_name: bookName,
        book_version: bookVersion,
        messageId,
        timestamp,
      };
    } catch {
      // If JSON parsing fails, return null
      return null;
    }
  }

  // Handle automation artifacts
  if (event.artifact && event.artifact.artifact_type === 'automation') {
    const content = event.artifact.content;
    if (!content) {
      return null;
    }

    let version: string | undefined;
    try {
      const automation = JSON.parse(content);
      version = automation.version;
    } catch {
      return null;
    }

    return {
      type: 'automation_built',
      messageId,
      timestamp,
      version,
    };
  }

  // Handle execution_reference artifacts
  if (
    event.artifact &&
    event.artifact.artifact_type === 'execution_reference'
  ) {
    const content = event.artifact.content;
    if (!content) {
      return null;
    }

    try {
      const executionRef = JSON.parse(content);
      const executionId = executionRef.execution_id;

      if (!executionId) {
        return null;
      }

      // Return custom format for execution reference
      return {
        type: 'execution_reference',
        executionId,
        outputValues: executionRef.output_values,
        messageId,
        timestamp,
      };
    } catch {
      // Fallback if parsing fails - try using content directly as executionId
      if (content.trim()) {
        return {
          type: 'execution_reference',
          executionId: content,
          messageId,
          timestamp,
        };
      }
      return null;
    }
  }

  // Handle default_inputs artifacts (silent side-effect, no visible message)
  if (event.artifact && event.artifact.artifact_type === 'default_inputs') {
    return {
      type: 'default_inputs',
      messageId,
      timestamp,
    };
  }

  // Handle suggested_prompts artifact: return a message with button-group widget only.
  // Guard on artifact type alone so empty/missing content does not fall through to other handlers.
  if (event.artifact?.artifact_type === 'suggested_prompts') {
    if (!event.artifact.content) return null;
    try {
      const parsed = JSON.parse(event.artifact.content) as unknown;
      const promptList = Array.isArray(parsed)
        ? (parsed as string[]).filter(
            (p): p is string => typeof p === 'string' && p.length > 0,
          )
        : [];
      if (promptList.length > 0) {
        return {
          id: messageId,
          content: '',
          direction: EMessageDirection.INCOMING,
          timestamp,
          widget: {
            type: EWidgetType.BUTTON_GROUP,
            buttons: promptList.map((prompt, index) => ({
              id: `suggested-prompt-${messageId}-${index}`,
              label: prompt,
              variant: 'outline' as const,
            })),
          },
        };
      }
    } catch (error) {
      captureException(error as Error, {
        tags: {
          component: 'convertEventToChatMessage',
          artifact: 'suggested_prompts',
        },
      });
    }
    return null;
  }

  // Handle todo_progress artifacts emitted from Claude's TodoWrite tool.
  if (event.artifact?.artifact_type === 'todo_progress') {
    return createTodoProgressChatMessage({
      messageId,
      timestamp,
      content: event.artifact.content,
    });
  }

  if (event.state === 'STATE_ERROR') {
    const message: IChatMessage = {
      id: messageId,
      content: event.completion_response?.error,
      direction: EMessageDirection.INCOMING,
      timestamp,
      widget: {
        type: 'custom',
        customContent: (
          <ChatStatus state="error" text={event.completion_response?.error} />
        ),
      },
    };

    return message;
  }

  // Handle STATE_CANCELLED - the user stopped the response. Emit a muted info message in the chat.
  if (event.state === 'STATE_CANCELLED') {
    const message: IChatMessage = {
      id: messageId,
      direction: EMessageDirection.INCOMING,
      timestamp,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: (
          <Item variant="default" size="sm" className="py-2 pl-0">
            <ItemMedia>
              <Icon type="Info" className="size-3 text-muted-foreground" />
            </ItemMedia>
            <ItemContent>
              <Markdown
                textProps={{
                  level: 'xSmall',
                  color: 'muted',
                  weight: 'normal',
                }}
              >
                {i18n.t('automation.quillChat.message.responseStopped', {
                  ns: 'automation',
                })}
              </Markdown>
            </ItemContent>
          </Item>
        ),
      },
    };

    return message;
  }

  // Handle STATE_COMPLETE - signals processing is done
  // If user_feedback is present, don't show the state_complete message
  if (event.state === 'STATE_COMPLETE' && !event.user_feedback) {
    return {
      type: 'state_complete',
      messageId,
      timestamp,
    };
  }

  return null;
}

/**
 * Processes an incoming thread message event and converts it to an IChatMessage.
 *
 * Handles different message types:
 * - connection_request: Creates a message with ChatIntegrationConnection widget, checks queue for matching add_connection
 * - add_connection: Updates existing connection_request with connectionId (returns null)
 * - default_inputs: Calls onDefaultInputsReceived callback to invalidate automation query (returns null)
 * - build_automation: Creates a message with automation build request widget
 * - execution_reference: Creates a message with QuillChatOutputRenderer widget
 * - Regular messages: Returns the converted IChatMessage directly
 *
 * @param {IProcessThreadMessageParams} params - Parameters for processing the message
 * @returns {IChatMessage | null} The processed chat message or null if the event cannot be processed
 */
export function processIncomingThreadMessage({
  event,
  onConnectionSelect,
  onAutomationBuilt,
  onFileClick,
  onAddConnection,
  onDefaultInputsReceived,
  addConnectionQueue,
  threadId,
  automationId,
}: IProcessThreadMessageParams): IChatMessage | null {
  const incomingMessage = convertEventToChatMessage(event, onFileClick);
  if (!incomingMessage) {
    return null;
  }

  // Handle add_connection - update existing message, don't return new message
  if ('type' in incomingMessage && incomingMessage.type === 'add_connection') {
    onAddConnection?.(
      incomingMessage.message_id,
      incomingMessage.connection_id,
      incomingMessage.book_name,
      incomingMessage.book_version,
    );
    return null;
  }

  // Handle default_inputs - silent side-effect, no visible message
  if ('type' in incomingMessage && incomingMessage.type === 'default_inputs') {
    onDefaultInputsReceived?.(true);
    return null;
  }

  // Check if it's a connection request message
  if (
    'type' in incomingMessage &&
    incomingMessage.type === 'connection_request'
  ) {
    // Check if there's a matching add_connection in the queue
    const addConnection = addConnectionQueue?.get(incomingMessage.messageId);

    if (addConnection && addConnectionQueue) {
      // Create connection_request message with connectionId already set (connected state)
      // Remove from queue since it's been matched
      addConnectionQueue.delete(incomingMessage.messageId);

      return {
        id: incomingMessage.messageId,
        direction: EMessageDirection.INCOMING,
        timestamp: incomingMessage.timestamp,
        widget: {
          type: EWidgetType.CUSTOM,
          customContent: (
            <ChatIntegrationConnection
              integration_name={addConnection.bookName}
              integration_version={addConnection.bookVersion}
              connectionId={addConnection.connectionId}
            />
          ),
        },
      };
    }

    // No matching add_connection found, create connection_request without connectionId
    return {
      id: incomingMessage.messageId,
      direction: EMessageDirection.INCOMING,
      timestamp: incomingMessage.timestamp,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: (
          <ChatIntegrationConnection
            integration_name={incomingMessage.book_name}
            integration_version={incomingMessage.book_version}
            requestId={incomingMessage.messageId}
            onSelectConnection={connection =>
              onConnectionSelect(
                connection,
                incomingMessage,
                incomingMessage.messageId,
              )
            }
          />
        ),
      },
    };
  }

  // Build automation request message
  if (
    'type' in incomingMessage &&
    incomingMessage.type === 'build_automation'
  ) {
    return {
      id: incomingMessage.messageId,
      direction: EMessageDirection.OUTGOING,
      timestamp: incomingMessage.timestamp,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: (
          <Item variant="outline" size="sm" className="py-2 px-3">
            <ItemMedia>
              <Icon type="Sparkles" className="size-4" />
            </ItemMedia>
            <ItemContent>
              <ItemDescription>
                {i18n.t('automation.quillChat.blueprint.buildAutomation', {
                  ns: 'automation',
                })}
              </ItemDescription>
            </ItemContent>
          </Item>
        ),
      },
    };
  }

  // Execution reference message
  if (
    'type' in incomingMessage &&
    incomingMessage.type === 'execution_reference'
  ) {
    return {
      id: incomingMessage.messageId,
      direction: EMessageDirection.INCOMING,
      timestamp: incomingMessage.timestamp,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: (
          <>
            <ChatBrowserSessionWidget
              executionId={incomingMessage.executionId}
              outputValues={incomingMessage.outputValues}
            />
            <ChatOutputRenderer executionId={incomingMessage.executionId} />
          </>
        ),
      },
    };
  }

  // Automation built message
  if (
    'type' in incomingMessage &&
    incomingMessage.type === 'automation_built'
  ) {
    onAutomationBuilt();
    return {
      id: incomingMessage.messageId,
      direction: EMessageDirection.INCOMING,
      timestamp: incomingMessage.timestamp,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: (
          <Item variant="outline" size="sm" className="py-2 !pl-0 !border-none">
            <ItemMedia>
              <Icon
                type="BadgeCheck"
                className="size-3 text-muted-foreground"
              />
            </ItemMedia>
            <ItemContent>
              <Markdown
                textProps={{
                  level: 'xSmall',
                  color: 'muted',
                  weight: 'normal',
                }}
              >
                {incomingMessage.version
                  ? i18n.t(
                      'automation.quillChat.message.automationBuiltVersion',
                      {
                        ns: 'automation',
                        version: incomingMessage.version,
                      },
                    )
                  : i18n.t('automation.quillChat.message.automationBuilt', {
                      ns: 'automation',
                    })}
              </Markdown>
            </ItemContent>
          </Item>
        ),
      },
    };
  }

  // State complete message - show feedback widget
  if ('type' in incomingMessage && incomingMessage.type === 'state_complete') {
    return {
      id: incomingMessage.messageId,
      direction: EMessageDirection.INCOMING,
      timestamp: incomingMessage.timestamp,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: threadId ? (
          <ChatFeedback
            messageId={incomingMessage.messageId}
            threadId={threadId}
            automationId={automationId}
          />
        ) : null,
      },
    };
  }

  // Regular chat message - already converted to IChatMessage
  if ('id' in incomingMessage && 'direction' in incomingMessage) {
    return incomingMessage as IChatMessage;
  }

  return null;
}

/**
 * Processes multiple thread messages in batch for initial load.
 * Collects all messages and determines final state, then returns results for a single state update.
 *
 * @param threadMessages - Array of thread messages to process in order
 * @param callbacks - Callbacks and configuration for processing
 * @returns Object containing new messages and final processing state
 */

/**
 * Builds a map from todo_progress message names to their owning user-turn
 * message names by walking the messages in chronological (oldest-first) order.
 *
 * @param threadMessages - Messages in API order (newest first)
 * @returns Map of todo message name → user-turn message name
 */
function buildTodoTurnMap(
  threadMessages: V1ThreadMessage[],
): Map<string, string> {
  const map = new Map<string, string>();
  let currentTurnId: string | undefined;
  for (const event of [...threadMessages].reverse()) {
    if ((event.user_message || event.state === 'STATE_QUEUED') && event.name) {
      currentTurnId = event.name;
    }
    if (
      event.artifact?.artifact_type === 'todo_progress' &&
      event.name &&
      currentTurnId
    ) {
      map.set(event.name, currentTurnId);
    }
  }
  return map;
}

/**
 * Processes batch messages and returns the final messages array
 * with all connection updates already applied.
 */
export function processBatchMessages(
  threadMessages: V1ThreadMessage[],
  callbacks: IBatchMessagesCallbacks,
): IBatchMessagesResult {
  const {
    onConnectionSelect,
    onFileClick,
    processedMessageIds,
    existingMessages,
    addConnectionQueue,
    threadId,
    automationId,
  } = callbacks;

  const newMessages: IChatMessage[] = [];
  let finalIsProcessing = false;
  let shouldInvalidateAutomation = false;

  const trackAutomationBuilt = () => {
    shouldInvalidateAutomation = true;
  };

  const trackAddConnection = (
    targetMessageId: string,
    connectionId: string,
    bookName: string,
    bookVersion: string,
  ) => {
    addConnectionQueue.set(targetMessageId, {
      connectionId,
      bookName,
      bookVersion,
    });
  };

  const todoTurnMap = buildTodoTurnMap(threadMessages);

  // threadMessages arrives newest-first — the first state-bearing event
  // determines whether the thread is still processing.
  let stateResolved = false;

  for (const event of threadMessages) {
    if (!stateResolved) {
      if (
        event.state === 'STATE_COMPLETE' ||
        event.state === 'STATE_ERROR' ||
        event.state === 'STATE_CANCELLED'
      ) {
        finalIsProcessing = false;
        stateResolved = true;
      } else if (event.state === 'STATE_QUEUED') {
        finalIsProcessing = true;
        stateResolved = true;
      }
    }

    const chatMessage = processIncomingThreadMessage({
      event,
      onConnectionSelect,
      onAutomationBuilt: trackAutomationBuilt,
      onFileClick,
      onAddConnection: trackAddConnection,
      addConnectionQueue,
      threadId,
      automationId,
    });

    if (
      chatMessage &&
      event.artifact?.artifact_type === 'todo_progress' &&
      event.name
    ) {
      const todoTurnId = todoTurnMap.get(event.name);
      if (todoTurnId) {
        chatMessage.id = getTodoProgressMessageId(todoTurnId);
      }
    }

    const messageId = chatMessage?.id;
    if (!messageId || processedMessageIds.has(messageId)) {
      continue;
    }

    processedMessageIds.add(messageId);
    newMessages.push(chatMessage);
  }

  const allMessages = finalIsProcessing
    ? reconcileToolCallActivity([...existingMessages, ...newMessages])
    : completeActiveToolCallMessages([...existingMessages, ...newMessages]);

  const finalMessages = mergeConsecutiveInternalToolCallMessages(
    allMessages.reverse(),
  );

  return {
    messages: finalMessages,
    isProcessing: finalIsProcessing,
    shouldInvalidateAutomation,
  };
}
